import { useEffect, useState, type FormEvent } from 'react'
import { api } from '../../api/client'
import { freigegeben } from './hilfen'
import { ARTEN, betriebstagHeute, type Art, type Verkehrsmeldung } from './typen'

interface Felder {
  nummer: string
  herausgeber: string
  art: Art
  titel: string
  ort: string
  anlass: string
  gueltigVon: string
  gueltigBis: string
  alleLinien: boolean
  linien: string
  gesperrteStrasse: string
  umleitungsstrecke: string
  entfallendeHaltestellen: string
  ersatzhaltestellen: string
  besonderheiten: string
  wichtig: boolean
}

function ausMeldung(m: Verkehrsmeldung | null): Felder {
  return {
    nummer: m?.nummer ?? '',
    herausgeber: m?.herausgeber ?? '',
    art: m?.art ?? 'UMLEITUNG',
    titel: m?.titel ?? '',
    ort: m?.ort ?? '',
    anlass: m?.anlass ?? '',
    // datetime-local will „JJJJ-MM-TTTHH:MM" — ohne Sekunden.
    gueltigVon: m ? m.gueltigVon.slice(0, 16) : `${betriebstagHeute()}T00:00`,
    gueltigBis: m?.gueltigBis ? m.gueltigBis.slice(0, 16) : '',
    alleLinien: m?.alleLinien ?? false,
    linien: m?.linien.join(', ') ?? '',
    gesperrteStrasse: m?.gesperrteStrasse ?? '',
    umleitungsstrecke: m?.umleitungsstrecke ?? '',
    entfallendeHaltestellen: m?.entfallendeHaltestellen ?? '',
    ersatzhaltestellen: m?.ersatzhaltestellen ?? '',
    besonderheiten: m?.besonderheiten ?? '',
    wichtig: m?.wichtig ?? false,
  }
}

/**
 * Die Maske — für neue Meldungen und zum Prüfen dessen, was automatisch kam.
 *
 * Beim Prüfen steht das PDF daneben: Wer eine Linie oder ein Datum kontrollieren soll,
 * muss das Original sehen, ohne zwischen Fenstern zu springen.
 */
export function Meldungsformular({
  meldung,
  onFertig,
}: {
  meldung: Verkehrsmeldung | null
  onFertig: (text?: string) => void | Promise<void>
}) {
  const [f, setF] = useState<Felder>(ausMeldung(meldung))
  const [fehler, setFehler] = useState<string | null>(null)
  const [sendet, setSendet] = useState(false)
  const [pdf, setPdf] = useState<string | null>(null)
  const [kurse, setKurse] = useState<{ kursnummer: string; linien: string[] }[] | null>(null)

  useEffect(() => {
    setF(ausMeldung(meldung))
    if (!meldung?.dateiname) {
      setPdf(null)
      return
    }
    let adresse: string | null = null
    let abgebrochen = false
    void api
      .holeBlob(`/api/verkehrsmeldungen/${meldung.id}/datei`)
      .then((b) => {
        if (abgebrochen) return
        // Ohne Seitenliste und auf Breite eingepasst: Der Aushang soll ohne Zutun lesbar
        // sein. Weitere Seiten erreicht man durch Scrollen.
        adresse = URL.createObjectURL(b) + '#navpanes=0&view=FitH'
        setPdf(adresse)
      })
      .catch(() => setPdf(null))
    return () => {
      abgebrochen = true
      // Ohne den Zusatz hinter der Raute kennt der Browser den Objekt-URL nicht mehr.
      if (adresse) URL.revokeObjectURL(adresse.split('#')[0])
    }
  }, [meldung])

  // Welche Kurse fahren die eingetragenen Linien? Beim Prüfen ist das die eigentliche
  // Frage: Betrifft uns die Meldung überhaupt? Eine leere Liste sagt mehr als jede
  // Linienangabe — dann hat die Auslesung etwas gelesen, das hier niemand fährt.
  useEffect(() => {
    const linien = f.linien.trim()
    if (!f.alleLinien && linien === '') {
      setKurse([])
      return
    }
    let abgebrochen = false
    // Kurz abwarten: Wer „73, 80.2" tippt, soll nicht bei jedem Zeichen eine Abfrage auslösen.
    const zeit = window.setTimeout(() => {
      void api
        .get<{ kursnummer: string; linien: string[] }[]>(
          `/api/verkehrsmeldungen/kurse?alleLinien=${f.alleLinien}&linien=${encodeURIComponent(linien)}`,
        )
        .then((k) => !abgebrochen && setKurse(k))
        .catch(() => !abgebrochen && setKurse(null))
    }, 400)
    return () => {
      abgebrochen = true
      window.clearTimeout(zeit)
    }
  }, [f.linien, f.alleLinien])

  const neu = meldung === null
  const imEingang = meldung?.status === 'ZU_PRUEFEN'
  const setze = <K extends keyof Felder>(k: K, v: Felder[K]) => setF({ ...f, [k]: v })

  function koerper() {
    const leer = (s: string) => (s.trim() === '' ? null : s.trim())
    return {
      nummer: leer(f.nummer),
      herausgeber: leer(f.herausgeber),
      art: f.art,
      titel: f.titel.trim(),
      ort: leer(f.ort),
      anlass: leer(f.anlass),
      gueltigVon: f.gueltigVon,
      gueltigBis: leer(f.gueltigBis),
      alleLinien: f.alleLinien,
      linien: f.alleLinien ? null : leer(f.linien),
      gesperrteStrasse: leer(f.gesperrteStrasse),
      umleitungsstrecke: leer(f.umleitungsstrecke),
      entfallendeHaltestellen: leer(f.entfallendeHaltestellen),
      ersatzhaltestellen: leer(f.ersatzhaltestellen),
      besonderheiten: leer(f.besonderheiten),
      wichtig: f.wichtig,
    }
  }

  async function absenden(ereignis: FormEvent, freigeben: boolean) {
    ereignis.preventDefault()
    setFehler(null)
    setSendet(true)
    try {
      if (neu) {
        const r = await api.post<{ benachrichtigt: number }>(
          `/api/verkehrsmeldungen?aktivieren=${freigeben}`,
          koerper(),
        )
        await onFertig(freigeben ? freigegeben(r.benachrichtigt) : 'Als Entwurf im Eingang abgelegt.')
        return
      }
      await api.put(`/api/verkehrsmeldungen/${meldung.id}`, koerper())
      if (freigeben) {
        const r = await api.post<{ benachrichtigt: number }>(
          `/api/verkehrsmeldungen/${meldung.id}/aktivieren`,
        )
        await onFertig(freigegeben(r.benachrichtigt))
      } else {
        await onFertig(
          meldung.status === 'AKTIV'
            ? 'Gespeichert. Die Fahrer sehen die Änderung am Dienst; eine neue Nachricht geht nicht hinaus.'
            : 'Gespeichert.',
        )
      }
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Speichern fehlgeschlagen')
    } finally {
      setSendet(false)
    }
  }

  async function erneutAuslesen() {
    if (!meldung) return
    try {
      await api.post(`/api/verkehrsmeldungen/${meldung.id}/erneut-auslesen`)
      await onFertig('Das PDF geht noch einmal an den Hausdienst.')
    } catch (ex) {
      setFehler(ex instanceof Error ? ex.message : 'Fehlgeschlagen')
    }
  }

  const textfeld = (k: keyof Felder, beschriftung: string, breit = false) => (
    <div className="feld" style={breit ? { gridColumn: '1 / -1' } : undefined}>
      <label>{beschriftung}</label>
      <input
        type="text"
        value={f[k] as string}
        onChange={(e) => setze(k, e.target.value as never)}
      />
    </div>
  )

  return (
    <div className={`karte abschnitt meldungsformular${pdf ? ' mit-pdf' : ''}`}>
      <div>
        <h2>
          {neu ? 'Neue Verkehrsmeldung' : imEingang ? 'Meldung prüfen' : 'Meldung bearbeiten'}
        </h2>
        {meldung?.auslesung === 'AUSSTEHEND' && (
          <div className="meldung hinweis">
            Das PDF wartet auf die Auslesung durch den Hausdienst. Sie können die Felder auch
            gleich selbst ausfüllen — dann wird nichts mehr überschrieben.
          </div>
        )}
        {meldung?.kiHinweis && <div className="meldung hinweis">{meldung.kiHinweis}</div>}
        {meldung?.eingangAbsender && (
          <p style={{ color: 'var(--grau-700)', fontSize: '0.85rem', marginTop: 0 }}>
            Weitergeleitet von {meldung.eingangAbsender}
            {meldung.eingangBetreff && <> · „{meldung.eingangBetreff}“</>}
            {meldung.kiModell && (
              <>
                {' '}
                · gelesen von {meldung.kiModell}
                {meldung.kiDauerMs ? ` in ${Math.round(meldung.kiDauerMs / 1000)} s` : ''}
              </>
            )}
          </p>
        )}
        {fehler && <div className="meldung fehler">{fehler}</div>}

        <form onSubmit={(e) => void absenden(e, neu)}>
          <div className="formular-raster">
            <div className="feld" style={{ gridColumn: '1 / -1' }}>
              <label>Titel *</label>
              <input
                type="text"
                required
                maxLength={200}
                value={f.titel}
                placeholder="z. B. Sperrung Senner Straße"
                onChange={(e) => setze('titel', e.target.value)}
              />
            </div>
            <div className="feld">
              <label>Art</label>
              <select value={f.art} onChange={(e) => setze('art', e.target.value as Art)}>
                {Object.entries(ARTEN).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="feld">
              <label>Wichtigkeit</label>
              <label className="schalter-zeile">
                <input
                  type="checkbox"
                  checked={f.wichtig}
                  onChange={(e) => setze('wichtig', e.target.checked)}
                />
                wichtig — hervorgehoben bei Fahrer und Disposition
              </label>
            </div>
            <div className="feld">
              <label>Gültig ab *</label>
              <input
                type="datetime-local"
                required
                value={f.gueltigVon}
                onChange={(e) => setze('gueltigVon', e.target.value)}
              />
            </div>
            <div className="feld">
              <label>Gültig bis</label>
              <input
                type="datetime-local"
                value={f.gueltigBis}
                onChange={(e) => setze('gueltigBis', e.target.value)}
              />
              <small>Leer: bis auf Weiteres. Ein ganzer letzter Tag endet um 04:00 des Folgetags.</small>
            </div>
            <div className="feld" style={{ gridColumn: '1 / -1' }}>
              <label>Betroffene Linien *</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="text"
                  value={f.linien}
                  disabled={f.alleLinien}
                  placeholder="z. B. 73, 80.2, N2"
                  style={{ flex: '1 1 220px' }}
                  required={!f.alleLinien}
                  onChange={(e) => setze('linien', e.target.value)}
                />
                <label className="schalter-zeile">
                  <input
                    type="checkbox"
                    checked={f.alleLinien}
                    onChange={(e) => setze('alleLinien', e.target.checked)}
                  />
                  alle Linien
                </label>
              </div>
              {kurse !== null && (
                <div style={{ marginTop: 6, fontSize: '0.85rem' }}>
                  {kurse.length === 0 ? (
                    <span style={{ color: 'var(--rot-500)' }}>
                      Kein Kurs des Betriebs fährt diese Linien — bitte prüfen, ob die Meldung
                      uns überhaupt betrifft.
                    </span>
                  ) : (
                    <>
                      <span style={{ color: 'var(--grau-700)' }}>
                        {kurse.length} betroffene{kurse.length === 1 ? 'r Kurs' : ' Kurse'}:{' '}
                      </span>
                      {kurse.map((k) => (
                        <span
                          key={k.kursnummer}
                          className="etikett navy"
                          title={`Linien ${k.linien.join(', ')}`}
                          style={{ marginRight: 4 }}
                        >
                          {k.kursnummer}
                        </span>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
            {textfeld('gesperrteStrasse', 'Gesperrte Straße', true)}
            {textfeld('umleitungsstrecke', 'Umleitungsstrecke', true)}
            {textfeld('entfallendeHaltestellen', 'Entfallende Haltestellen')}
            {textfeld('ersatzhaltestellen', 'Ersatzhaltestellen')}
            <div className="feld" style={{ gridColumn: '1 / -1' }}>
              <label>Besonderheiten</label>
              <textarea
                rows={2}
                value={f.besonderheiten}
                onChange={(e) => setze('besonderheiten', e.target.value)}
              />
            </div>
            {textfeld('ort', 'Ort')}
            {textfeld('anlass', 'Anlass')}
            {textfeld('herausgeber', 'Herausgeber')}
            {textfeld('nummer', 'Nummer')}
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
            {neu ? (
              <>
                <button type="submit" className="knopf-primaer" disabled={sendet}>
                  Anlegen und freigeben
                </button>
                <button
                  type="button"
                  className="knopf-schlicht"
                  disabled={sendet}
                  onClick={(e) => void absenden(e, false)}
                >
                  Als Entwurf speichern
                </button>
              </>
            ) : imEingang ? (
              <>
                <button
                  type="button"
                  className="knopf-primaer"
                  disabled={sendet}
                  onClick={(e) => void absenden(e, true)}
                >
                  Speichern und freigeben
                </button>
                <button
                  type="button"
                  className="knopf-schlicht"
                  disabled={sendet}
                  onClick={(e) => void absenden(e, false)}
                >
                  Nur speichern
                </button>
                {meldung.dateiname && meldung.auslesung !== 'AUSSTEHEND' && (
                  <button type="button" className="knopf-schlicht" onClick={() => void erneutAuslesen()}>
                    Erneut auslesen
                  </button>
                )}
              </>
            ) : (
              <button
                type="button"
                className="knopf-primaer"
                disabled={sendet}
                onClick={(e) => void absenden(e, false)}
              >
                Speichern
              </button>
            )}
            <button type="button" className="knopf-schlicht" onClick={() => void onFertig()}>
              Abbrechen
            </button>
          </div>
        </form>
      </div>

      {pdf && (
        <div className="meldungsformular-pdf">
          <iframe src={pdf} title="PDF der Meldung" />
        </div>
      )}
    </div>
  )
}
